import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";

const ENDPOINTS = {
  cargar: "/api/facturacion/config/firma/cargar/",
  clave: "/api/facturacion/config/firma/clave/",
  remover: "/api/facturacion/config/firma/remover/",
};

const ElectronicSignatureSettings = ({
  firmaEstado,
  onRefresh,
  authFetch,
  parseJsonSafe,
  notify,
  logout,
  fieldSx,
  primaryButtonSx,
  outlinedButtonSx,
}) => {
  const { dashboardTheme } = useDashboardTheme();
  const fileRef = useRef(null);

  const [clave, setClave] = useState("");
  const [editando, setEditando] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);

  const firmaSubida = !!firmaEstado?.subida;
  const firmaValida = !!firmaEstado?.valida;
  const firmadoAutomatico = !!firmaEstado?.firmado_automatico;

  const cardSx = {
    borderRadius: "14px",
    p: 2,
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    boxShadow:
      dashboardTheme.theme_mode === "dark"
        ? "none"
        : "0 4px 10px rgba(0,0,0,0.08)",
  };

  const softBoxSx = {
    p: 1.5,
    borderRadius: "12px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
  };

  const onPickFile = () => {
    if (uploading || removing) return;
    fileRef.current?.click();
  };

  const onFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = String(file.name || "").split(".").pop().toLowerCase();

    if (!["p12", "pfx"].includes(ext)) {
      notify("Solo se permite archivo .p12 o .pfx", "error");
      event.target.value = "";
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("archivo", file);

      const res = await authFetch(ENDPOINTS.cargar, {
        method: "POST",
        body: formData,
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo cargar la firma electrónica.", "error");
        return;
      }

      setClave("");
      setEditando(true);
      await onRefresh();
      notify(data?.message || "Firma electrónica cargada correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const guardarClave = async () => {
    if (!firmaSubida) {
      notify("Primero sube la firma electrónica.", "error");
      return;
    }

    if (!String(clave || "").trim()) {
      notify("Ingresa la clave de la firma.", "error");
      return;
    }

    try {
      setSavingKey(true);

      const formData = new FormData();
      formData.append("clave", clave);

      const res = await authFetch(ENDPOINTS.clave, {
        method: "POST",
        body: formData,
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "Clave incorrecta o firma inválida.", "error");
        return;
      }

      setClave("");
      setEditando(false);
      await onRefresh();
      notify(data?.message || "Firma validada correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSavingKey(false);
    }
  };

  const removerFirma = async () => {
    try {
      setRemoving(true);

      const res = await authFetch(ENDPOINTS.remover, {
        method: "POST",
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo remover la firma electrónica.", "error");
        return;
      }

      setClave("");
      setEditando(false);
      await onRefresh();
      notify(data?.message || "Firma electrónica removida correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setRemoving(false);
    }
  };

  const toggleAuto = async (checked) => {
    try {
      setSavingAuto(true);

      const formData = new FormData();
      formData.append("auto", checked ? "1" : "0");

      const res = await authFetch(ENDPOINTS.clave, {
        method: "POST",
        body: formData,
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo guardar la firma automática.", "error");
        return;
      }

      await onRefresh();
      notify(data?.message || "Configuración guardada correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSavingAuto(false);
    }
  };

  return (
    <Paper elevation={0} sx={cardSx}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  dashboardTheme.theme_mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : `${dashboardTheme.primary_color}14`,
              }}
            >
              <VpnKeyRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 17, md: 18 },
                  fontWeight: 600,
                  color: dashboardTheme.text_color,
                }}
              >
                Firma Electrónica
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  color: dashboardTheme.muted_text_color,
                  lineHeight: 1.5,
                }}
              >
                Carga la firma del representante legal y valida su clave.
              </Typography>
            </Box>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
            {(!firmaValida || editando) && (
              <Button
                variant="contained"
                startIcon={uploading ? <CircularProgress size={17} /> : <FolderOpenRoundedIcon />}
                onClick={onPickFile}
                disabled={uploading || removing}
                sx={primaryButtonSx}
              >
                {uploading ? "Subiendo..." : "Seleccionar firma"}
              </Button>
            )}

            {firmaValida && !editando && (
              <Button
                variant="outlined"
                startIcon={<EditRoundedIcon />}
                onClick={() => setEditando(true)}
                disabled={removing}
                sx={outlinedButtonSx}
              >
                Modificar firma
              </Button>
            )}

            {firmaSubida && (
              <Button
                variant="outlined"
                color="error"
                startIcon={removing ? <CircularProgress size={17} /> : <DeleteOutlineRoundedIcon />}
                onClick={removerFirma}
                disabled={removing || uploading}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {removing ? "Removiendo..." : "Remover"}
              </Button>
            )}
          </Stack>
        </Box>

        <input
          ref={fileRef}
          type="file"
          accept=".p12,.pfx"
          hidden
          onChange={onFileChange}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            gap: 1.5,
          }}
        >
          <Paper elevation={0} sx={softBoxSx}>
            <Typography sx={{ fontSize: 13, color: dashboardTheme.muted_text_color, fontWeight: 600 }}>
              Archivo
            </Typography>
            <Typography sx={{ fontSize: 15, color: dashboardTheme.text_color, fontWeight: 700 }}>
              {firmaSubida ? "Cargado" : "Sin cargar"}
            </Typography>
          </Paper>

          <Paper elevation={0} sx={softBoxSx}>
            <Typography sx={{ fontSize: 13, color: dashboardTheme.muted_text_color, fontWeight: 600 }}>
              Estado
            </Typography>
            <Typography sx={{ fontSize: 15, color: firmaValida ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
              {firmaValida ? "Validada" : "Pendiente"}
            </Typography>
          </Paper>

          <Paper elevation={0} sx={softBoxSx}>
            <Typography sx={{ fontSize: 13, color: dashboardTheme.muted_text_color, fontWeight: 600 }}>
              Válida hasta
            </Typography>
            <Typography sx={{ fontSize: 15, color: dashboardTheme.text_color, fontWeight: 700 }}>
              {firmaEstado?.valido_hasta || "-"}
            </Typography>
          </Paper>
        </Box>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          <TextField
            type="password"
            size="small"
            label="Clave de la firma"
            disabled={(firmaValida && !editando) || savingKey}
            value={clave}
            onChange={(event) => setClave(event.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", md: 320 } }}
          />

          {(!firmaValida || editando) && (
            <Button
              variant="contained"
              startIcon={savingKey ? <CircularProgress size={17} /> : <VerifiedUserRoundedIcon />}
              onClick={guardarClave}
              disabled={savingKey || !firmaSubida}
              sx={primaryButtonSx}
            >
              {savingKey ? "Validando..." : "Validar"}
            </Button>
          )}
        </Stack>

        <Paper elevation={0} sx={softBoxSx}>
          <FormControlLabel
            control={
              <Switch
                checked={firmadoAutomatico}
                onChange={(event) => toggleAuto(event.target.checked)}
                disabled={!firmaValida || savingAuto}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: dashboardTheme.primary_color,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: dashboardTheme.primary_color,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                  Firma automática
                </Typography>
                <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 12 }}>
                  Permite firmar automáticamente los XML cuando se emita la factura.
                </Typography>
              </Box>
            }
          />
        </Paper>
      </Stack>
    </Paper>
  );
};

export default ElectronicSignatureSettings;